import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid'; // Import uuid library for persistent user IDs

// IMPORTANT: Replace this with the actual URL to your PHP API folder on cPanel
const API_BASE_URL = 'https://survey.nodeinnovations.com/api';

// Define the full URL for the logo using API_BASE_URL
const LOGO_URL = `${API_BASE_URL}/images/logo_feather_dhara.png`; // Corrected logo URL

function App() {
    // --- State for overall survey flow ---
    const [surveyStarted, setSurveyStarted] = useState(false); // Controls welcome page vs. survey content
    const [loading, setLoading] = useState(true); // For initial image fetching and review submission
    const [error, setError] = useState(''); // For display errors

    // --- State for image display and navigation ---
    const [imagesToReview, setImagesToReview] = useState([]); // All images fetched from backend
    const [currentImageIndex, setCurrentImageIndex] = useState(0); // Index of the currently displayed image

    // --- User ID and reviewed images tracking for client-side persistence ---
    const [userId, setUserId] = useState(null);
    const [reviewedImagesByUser, setReviewedImagesByUser] = useState({}); // Stores which images THIS user has reviewed

    // --- Coupon and Copy functionality state ---
    const couponCode = "FIRSTVOICE"; // Changed coupon code to match the image
    const [copyStatus, setCopyStatus] = useState(''); // To show "Copied!" message

    // Effect to initialize User ID and fetch ALL images from the backend
    useEffect(() => {
        // 1. Generate/Load User ID
        let id = localStorage.getItem('surveyApp_userId'); // Use a consistent key
        if (!id) {
            id = uuidv4(); // Generate a new UUID
            localStorage.setItem('surveyApp_userId', id); // Store it
        }
        setUserId(id); // Set to state

        // 2. Load previously reviewed images from localStorage for this user
        const savedReviewed = JSON.parse(localStorage.getItem('surveyApp_reviewedImages') || '{}');
        setReviewedImagesByUser(savedReviewed);

        // 3. Only fetch images if survey is started (after welcome page)
        if (surveyStarted) {
            const fetchAllImages = async () => {
                setLoading(true);
                setError('');
                try {
                    const response = await axios.get(`${API_BASE_URL}/get_images.php`);
                    if (response.data.success && response.data.images) {
                        const fetchedImages = response.data.images.map(img => ({
                            unique_image_id: img.unique_image_id, // Ensure this ID is available
                            filename: img.filename,
                            imageUrl: `${API_BASE_URL}/${img.image_path}`, // Construct full URL
                            // Not using uploadedBy, vendorName from get_images.php for display on frontend
                        }));

                        // Filter out images already reviewed by this user
                        const unreviewedImages = fetchedImages.filter(img => !savedReviewed[img.unique_image_id]);

                        setImagesToReview(unreviewedImages);
                        if (unreviewedImages.length === 0) {
                            setError('You’ve reviewed all the pieces. Your input means the world as we build something beautiful, together.');
                        } else {
                            // Ensure currentImageIndex starts at 0 for the filtered list
                            setCurrentImageIndex(0); // Always start from the first unreviewed image
                        }
                    } else {
                        setError(response.data.message || 'No images found or an error occurred.');
                        setImagesToReview([]);
                    }
                } catch (err) {
                    console.error('Error fetching images:', err);
                    setError('Network error: Could not load images. Please try again later.');
                    setImagesToReview([]);
                } finally {
                    setLoading(false);
                }
            };
            fetchAllImages();
        }
    }, [surveyStarted]); // Re-run effect when surveyStarted changes or on initial mount

    // --- Handlers for survey flow ---
    const handleStartSurvey = () => {
        setSurveyStarted(true); // Move from welcome page to survey content
        // useEffect will now trigger image fetching
    };

    // Function to handle the submission of a review
    const handleSubmitReview = async (rating) => {
        if (loading || !userId) {
            console.warn("Cannot submit review: Loading or User ID not available.");
            return;
        }

        const currentImage = imagesToReview[currentImageIndex];
        if (!currentImage) {
            console.warn("No current image to review.");
            return;
        }

        // Client-side check for already reviewed image
        if (reviewedImagesByUser[currentImage.unique_image_id]) {
            console.log("Image already reviewed by this user. Skipping submission and moving to next.");
            // Immediately remove the already reviewed image and move to next
            const remainingImages = imagesToReview.filter(
                img => img.unique_image_id !== currentImage.unique_image_id
            );
            setImagesToReview(remainingImages);
            // If there are still images left, the next one will be at index 0 of the new array
            if (remainingImages.length > 0) {
                setCurrentImageIndex(0);
            }
            setError(''); // Clear any previous errors
            return;
        }

        setLoading(true); // Show loading spinner during submission

        const reviewData = {
            unique_image_id: currentImage.unique_image_id, // Use unique_image_id
            rating: rating,
            userId: userId, // Send the unique userId with the review
        };

        try {
            const response = await axios.post(`${API_BASE_URL}/submit_review.php`, reviewData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });

            if (response.data.success) {
                console.log(`Review '${rating}' for ${currentImage.filename} submitted successfully!`);

                // Mark image as reviewed for this user and save to localStorage
                const updatedReviewed = {
                    ...reviewedImagesByUser,
                    [currentImage.unique_image_id]: true // Mark this image ID as reviewed
                };
                setReviewedImagesByUser(updatedReviewed);
                localStorage.setItem('surveyApp_reviewedImages', JSON.stringify(updatedReviewed));

                // --- IMPORTANT CHANGE HERE: IMMEDIATELY UPDATE UI ---
                // Filter out the reviewed image from the current list in state
                const remainingImages = imagesToReview.filter(
                    img => img.unique_image_id !== currentImage.unique_image_id
                );
                setImagesToReview(remainingImages); // Update the list

                // If there are still images left, set current index to 0 for the next image in the filtered list
                // Otherwise, the 'allImagesReviewed' state will handle the display
                if (remainingImages.length > 0) {
                    setCurrentImageIndex(0);
                }
                setError(''); // Clear any previous errors

            } else {
                console.error("Error submitting review:", response.data.message);
                setError(response.data.message || "Failed to submit review.");
                // If backend says 'already reviewed', also update frontend state and remove from list
                if (response.data.message && response.data.message.includes("already reviewed")) {
                    const updatedReviewed = {
                        ...reviewedImagesByUser,
                        [currentImage.unique_image_id]: true
                    };
                    setReviewedImagesByUser(updatedReviewed);
                    localStorage.setItem('surveyApp_reviewedImages', JSON.stringify(updatedReviewed));

                    const remainingImages = imagesToReview.filter(
                        img => img.unique_image_id !== currentImage.unique_image_id
                    );
                    setImagesToReview(remainingImages);
                    if (remainingImages.length > 0) {
                        setCurrentImageIndex(0);
                    }
                    setError('');
                }
            }
        } catch (error) {
            console.error("Network error submitting review:", error);
            setError("Network error submitting review. Please try again.");
        } finally {
            setLoading(false); // Hide loading spinner
        }
    };

    // Function to copy text to clipboard
    const copyToClipboard = (text) => {
        // Fallback for older browsers or environments where navigator.clipboard is not available
        if (!navigator.clipboard || !navigator.clipboard.writeText) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed'; // Avoid scrolling to bottom
            textarea.style.opacity = '0'; // Hide textarea
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            try {
                document.execCommand('copy');
                setCopyStatus('Copied!');
                setTimeout(() => setCopyStatus(''), 2000); // Clear message after 2 seconds
            } catch (err) {
                console.error('Fallback copy failed:', err);
                setCopyStatus('Failed to copy.');
            } finally {
                document.body.removeChild(textarea);
            }
            return;
        }

        // Use modern Clipboard API
        navigator.clipboard.writeText(text).then(() => {
            setCopyStatus('Copied!');
            setTimeout(() => setCopyStatus(''), 2000); // Clear message after 2 seconds
        }).catch(err => {
            console.error('Failed to copy to clipboard:', err);
            setCopyStatus('Failed to copy.');
        });
    };


    const currentImage = imagesToReview[currentImageIndex];
    const allImagesReviewed = imagesToReview.length === 0 && !loading && surveyStarted && !error; // Corrected logic for all reviewed
    const noImagesAvailableInitially = imagesToReview.length === 0 && !loading && surveyStarted && !error && !allImagesReviewed;


    // --- Main Conditional Rendering Logic ---

    // 1. Show Welcome Page
    if (!surveyStarted) {
        return (
            <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4 font-sans">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-8 text-center">
                    {/* LOGO on Welcome Screen - at the very top */}
                    <div className="py-4 text-center">
                        <img src={LOGO_URL} alt="Company Logo" className="h-10 inline-block" /> {/* Changed h-16 to h-10 */}
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 leading-tight">Your voice matters.</h1>
                    <p className="text-gray-700 text-lg md:text-xl mb-6 leading-relaxed">
                        We’re building a brand that celebrates you - the modern woman who is <b>Strong</b> and <b>Still</b>, <b>Radiant</b> and <b>Real</b>.
                    </p>
                    <p className="text-gray-700 text-base md:text-lg mb-8 leading-relaxed">
                        Take just 2 minutes to share your thoughts on these collections.
                    </p>
                    <p className="text-gray-700 text-base md:text-lg mb-10 leading-relaxed">
                        As a thank you for your time, you’ll receive a special <b>coupon code</b> at the end, our way of saying we value your voice 💌.
                    </p>
                    <button
                        onClick={handleStartSurvey}
                        className="bg-[#bf7e58] text-white px-8 py-4 rounded-full text-xl font-semibold hover:bg-[#895a42] transition duration-200 shadow-lg"
                    >
                        Start Survey
                    </button>
                </div>
            </div>
        );
    }

    // 2. Show Loading/Error/No Images/All Reviewed messages
    if (loading || error || allImagesReviewed || noImagesAvailableInitially) {
        return (
            <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4 font-sans">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-sm h-[90vh] flex flex-col items-center justify-center text-center">
                    {/* LOGO on Loading/Error/All Reviewed Screens - header middle */}
                    <div className="py-4 text-center w-full">
                        <img src={LOGO_URL} alt="Company Logo" className="h-10 inline-block" /> {/* Changed h-16 to h-10 */}
                    </div>
                    {loading && !error && (
                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent mb-4"></div>
                    )}
                    {error && (
                        <div className="text-red-600 text-lg px-4">
                            <p>{error}</p>
                            <button
                                onClick={handleStartSurvey} // Re-fetch images on error 'Try Again'
                                className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                            >
                                Try Again
                            </button>
                        </div>
                    )}
                    {!loading && !error && noImagesAvailableInitially && (
                        <div className="text-xl text-gray-600 px-4">
                            <p>No images available for review.</p>
                            <p className="mt-2 text-base">Please upload images via the Admin Portal.</p>
                        </div>
                    )}
                    {!loading && !error && allImagesReviewed && (
                        <div className="flex flex-col items-center justify-center h-full px-4 py-8">
                            <h2 className="text-3xl md:text-4xl font-bold text-green-700 mb-4 animate-bounce">
                                Thank You!
                            </h2>
                            <p className="text-gray-700 text-lg md:text-xl mb-6 text-center">
                                Your feedback is invaluable to us. As a token of our appreciation, please enjoy this special discount on your first purchase!
                            </p>
                            
                            {/* Coupon Code Block - Matches image */}
                            <p className="text-gray-700 text-base mb-2">Tap to copy code</p>
                            <div className="flex items-center bg-white border-2 border-dashed border-gray-400 rounded-md p-2 shadow-md">
                                <span className="text-2xl sm:text-3xl font-bold text-gray-900 px-2 select-all cursor-text">
                                    {couponCode}
                                </span>
                                <button
                                    onClick={() => copyToClipboard(couponCode)}
                                    className="ml-2 px-4 py-2 bg-[#bf7e58] text-white font-semibold rounded-md hover:bg-[#895a42] transition duration-200"
                                    aria-label="Copy coupon code"
                                >
                                    COPY
                                </button>
                            </div>
                            {copyStatus && (
                                <p className="text-sm mt-2 text-blue-600 font-semibold">{copyStatus}</p>
                            )}

                        </div>
                    )}
                </div>
            </div>
        );
    }

    // 3. Show Survey Content (Image and Buttons)
    return (
        <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-start p-2 pt-8 font-sans">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm h-[75vh] flex flex-col overflow-hidden">
                <div className="py-2 text-center border-b border-gray-200">
                    <img src={LOGO_URL} alt="Company Logo" className="h-8 inline-block" />
                </div>
                <div className="flex-grow flex items-center justify-center px-2 pt-0.5 mb-2 relative h-3/4">
                    <img
                        src={currentImage?.imageUrl || 'https://placehold.co/600x450/CCCCCC/333333?text=Loading...'}
                        alt={`Image: ${currentImage?.filename || 'N/A'}`}
                        className="max-w-full max-h-full object-contain rounded-md"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://placehold.co/600x450/FF0000/FFFFFF?text=Image+Error';
                        }}
                    />
                </div>

                <div className="flex-shrink-0 flex items-center justify-around px-2 py-0 pt-10 mb-10 bg-gray-50 border-t border-gray-200">
                    <>
                        <button
                            onClick={() => handleSubmitReview(0)}
                            disabled={loading}
                            className="flex-1 mx-1 py-2 px-1 bg-red-500 text-white font-semibold rounded-lg shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            Bad
                        </button>
                        <button
                            onClick={() => handleSubmitReview(1)}
                            disabled={loading}
                            className="flex-1 mx-1 py-2 px-1 bg-yellow-500 text-gray-900 font-semibold rounded-lg shadow-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus://ring-yellow-500 focus:ring-opacity-75 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            Average
                        </button>
                        <button
                            onClick={() => handleSubmitReview(2)}
                            disabled={loading}
                            className="flex-1 mx-1 py-2 px-1 bg-green-500 text-white font-semibold rounded-lg shadow-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            Good
                        </button>
                    </>
                </div>
            </div>
        </div>
    );
}

export default App;